import React from 'react';

import { InfoCircleIcon } from '@vlabs/icons';
import {
  FaceSample, BodySample, Tooltip, useWindowSize,
} from '@vlabs/uikit';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useEventQuery } from '@vlabs/monokit/entities/events/api';
import type { EventListPermissions } from '@vlabs/monokit/entities/events/event-list-table/EventListPermissions';
import { GoToEventDetailsCircleAnimatedButton } from '@vlabs/monokit/features/go-to-event-details';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useSampleCellTranslation } from './i18n';
import st from './SampleCell.module.sass';

const displayName = 'SampleCell';
const testid = buildTestid(displayName);

interface SampleCellProps extends WithClassNameProp, WithDataTestid {
  eventId: string;
  permissions?: Partial<EventListPermissions>;
}

function SampleCell({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
  permissions = {},
}: SampleCellProps): React.JSX.Element {
  const { t } = useSampleCellTranslation();
  const windowSize = useWindowSize();
  const canViewEventDetails = permissions.canViewEventDetails ?? true;

  const { data: event } = useEventQuery(eventId);

  const hasEvent = !!event;
  const hasBody = !!event?.body_detections?.length;

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceDetectionUrl = faceDetection?.uiSampleURL;

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetectionUrl = bodyDetection?.uiSampleURL;

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      {!!canViewEventDetails && (
        <div className={st.GoToButton}>
          <GoToEventDetailsCircleAnimatedButton
            eventId={eventId}
            size="small"
            {...testid(dataTestId)}
          />
        </div>
      )}

      {!!hasEvent && (
        <FaceSample
          className={st.Sample}
          placeholder={(
            <BaseSamplePlaceholder
              placeholderType="image"
              tooltip={(
                <Tooltip tooltipContent={t('tooltip_empty_sample')}>
                  <span className={st.TooltipIcon}>
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
              )}
            />
          )}
          size={windowSize.width && windowSize.width < 1920 ? 'medium' : 'large'}
          src={faceDetectionUrl}
        />
      )}

      {!!hasBody && (
        <BodySample
          className={st.Sample}
          placeholder={(
            <BaseSamplePlaceholder
              placeholderType="image"
              size="large"
              tooltip={(
                <Tooltip tooltipContent={t('tooltip_empty_sample')}>
                  <span className={st.TooltipIcon}>
                    <InfoCircleIcon />
                  </span>
                </Tooltip>
                )}
            />
            )}
          size={windowSize.width && windowSize.width < 1920 ? 'medium' : 'large'}
          src={bodyDetectionUrl}
        />
      )}
    </div>
  );
}

SampleCell.displayName = displayName;

export { SampleCell };
